import { useCallback, useEffect, useSyncExternalStore } from "react";
import { api, ApiError } from "./api";

type Snapshot = { data: unknown; error: string };
type Entry = {
  snapshot: Snapshot;
  listeners: Set<() => void>;
  request?: Promise<void> | undefined;
  generation: number;
};
const empty: Snapshot = { data: null, error: "" };
// Session memory only: private responses never enter persistent browser storage.
const cache = new Map<string, Entry>();
function entryFor(path: string): Entry {
  let entry = cache.get(path);
  if (!entry) {
    entry = { snapshot: empty, listeners: new Set(), generation: 0 };
    cache.set(path, entry);
  }
  return entry;
}
function publish(entry: Entry, snapshot: Snapshot) {
  entry.snapshot = snapshot;
  entry.listeners.forEach((listener) => listener());
}
async function load(path: string, entry = entryFor(path)) {
  if (entry.request) return entry.request;
  const generation = entry.generation;
  entry.request = (async () => {
    try {
      const data = await api(path);
      if (generation === entry.generation) publish(entry, { data, error: "" });
    } catch (error) {
      if (generation !== entry.generation) return;
      if (error instanceof ApiError && error.status === 401) {
        clearDataCache();
        void load("/auth/state");
      } else {
        publish(entry, {
          data: entry.snapshot.data,
          error: (error as Error).message,
        });
      }
    } finally {
      if (generation === entry.generation) entry.request = undefined;
    }
  })();
  return entry.request;
}
export function clearDataCache() {
  for (const [path, entry] of cache) {
    entry.generation++;
    entry.request = undefined;
    publish(entry, empty);
    if (!entry.listeners.size) cache.delete(path);
  }
}
function invalidateData() {
  for (const [path, entry] of cache) {
    if (path.startsWith("/auth/")) continue;
    entry.generation++;
    entry.request = undefined;
    if (entry.listeners.size) void load(path, entry);
    else cache.delete(path);
  }
}
window.addEventListener("tedalytics:data-changed", invalidateData);

export function useData<T>(path: string | null, interval = 0) {
  const subscribe = useCallback(
    (listener: () => void) => {
      if (!path) return () => {};
      const entry = entryFor(path);
      entry.listeners.add(listener);
      // Touch on use, then bound inactive page/filter entries without evicting live readers.
      cache.delete(path);
      cache.set(path, entry);
      for (const [key, old] of cache) {
        if (cache.size <= 150) break;
        if (!old.listeners.size && !old.request) cache.delete(key);
      }
      return () => {
        entry.listeners.delete(listener);
      };
    },
    [path],
  );
  const getSnapshot = useCallback(
    () => (path ? entryFor(path).snapshot : empty),
    [path],
  );
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  const refresh = useCallback(() => {
    if (path) void load(path);
  }, [path]);
  useEffect(() => {
    if (!path) return;
    // Cached content is available during the very first render; refresh quietly.
    refresh();
    const timer = interval
      ? setInterval(() => {
          if (document.visibilityState === "visible") refresh();
        }, interval)
      : undefined;
    return () => clearInterval(timer);
  }, [path, interval, refresh]);
  return { data: snapshot.data as T | null, error: snapshot.error, refresh };
}
